import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9292/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

/* ── inject access token on every request ── */
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('radius_access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const normalizeResponse = (res) => {
  const h = res.headers;
  const total      = h['x-total']       ? parseInt(h['x-total'])        : null;
  const totalPages = h['x-total-pages'] ? parseInt(h['x-total-pages'])  : null;
  const page       = h['x-page']        ? parseInt(h['x-page'])         : null;
  const perPage    = h['x-per-page']    ? parseInt(h['x-per-page'])     : null;

  if (total !== null && res.data !== null && typeof res.data === 'object') {
    if (Array.isArray(res.data)) {
      return { _array: res.data, total, total_pages: totalPages, page, per_page: perPage };
    }
    return {
      ...res.data,
      ...(total      !== null && res.data.total       == null ? { total }       : {}),
      ...(totalPages !== null && res.data.total_pages == null ? { total_pages: totalPages } : {}),
      ...(page       !== null && res.data.page        == null ? { page }        : {}),
      ...(perPage    !== null && res.data.per_page    == null ? { per_page: perPage }    : {}),
    };
  }
  return res.data;
};

const clearSession = () => {
  localStorage.removeItem('radius_access_token');
  localStorage.removeItem('radius_refresh_token');
  localStorage.removeItem('radius_user');
  window.location.href = '/login';
};

let isRefreshing = false;
let refreshQueue = []; // pending requests waiting for new token

const processQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  refreshQueue = [];
};

api.interceptors.response.use(
  res => normalizeResponse(res),
  async err => {
    const original = err.config;

    /* 401 + belum retry → coba refresh token dulu */
    if (err?.response?.status === 401 && !original._retry) {
      const refreshToken = localStorage.getItem('radius_refresh_token');

      /* Tidak ada refresh token → langsung logout */
      if (!refreshToken) return clearSession();

      /* Jika sedang refresh, antrekan request ini */
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(token => {
          original.headers['Authorization'] = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
        const newAccessToken = data.access_token;

        localStorage.setItem('radius_access_token', newAccessToken);
        if (data.refresh_token) localStorage.setItem('radius_refresh_token', data.refresh_token);

        api.defaults.headers['Authorization'] = `Bearer ${newAccessToken}`;
        original.headers['Authorization']     = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return api(original); // retry request asli
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearSession();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    const msg = err?.response?.data?.error || err.message || 'Request failed';
    return Promise.reject(new Error(Array.isArray(msg) ? msg.join(', ') : msg));
  }
);

const normalizeList = (res, key) => {
  if (res && res._array) {
    return {
      [key]: res._array,
      total: res.total ?? res._array.length,
      page: res.page ?? 1,
      per_page: res.per_page ?? res._array.length,
      total_pages: res.total_pages ?? 1,
    };
  }
  if (Array.isArray(res)) {
    return { [key]: res, total: res.length, page: 1, per_page: res.length, total_pages: 1 };
  }
  return res;
};

export const authAPI = {
  login:          (data)  => api.post('/auth/login', data),
  setup:          (data)  => api.post('/auth/setup', data),
  refresh:        (data)  => api.post('/auth/refresh', data),
  me:             ()      => api.get('/auth/me'),
  changePassword: (data)  => api.patch('/auth/me', data),
};

export const radiusServersAPI = {
  list: (params) => api.get('/radius-servers', { params }).then(r => normalizeList(r, 'radius_servers')),
  get: (id) => api.get(`/radius-servers/${id}`),
  create: (data) => api.post('/radius-servers', data),
  update: (id, data) => api.patch(`/radius-servers/${id}`, data),
  delete:          (id, params)               => api.delete(`/radius-servers/${id}`, { params }),
  deletePermanent: (id)                        => api.delete(`/radius-servers/${id}/permanent`),
  restore: (id) => api.post(`/radius-servers/${id}/restore`),
  health: (id) => api.get(`/radius-servers/${id}/health`),
  healthHistory: (id, params) => api.get(`/radius-servers/${id}/health/history`, { params }),
  restart: (id) => api.post(`/radius-servers/${id}/restart`),
  checkAllHealth: () => api.post('/radius-servers/health/check-all'),
  healthSchedulerStatus: () => api.get('/radius-servers/health/scheduler-status'),
  stats: (id) => api.get(`/radius-servers/${id}/stats`),
  tenants: (id) => api.get(`/radius-servers/${id}/tenants`).then(r => normalizeList(r, 'tenants')),
};

export const tenantsAPI = {
  list: (params) => api.get('/tenants', { params }).then(r => normalizeList(r, 'tenants')),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  delete:          (id, params)               => api.delete(`/tenants/${id}`, { params }),
  deletePermanent: (id)                        => api.delete(`/tenants/${id}/permanent`),
  restore: (id) => api.post(`/tenants/${id}/restore`),
};

export const usersAPI = {
  list: (tenantId, params) => api.get(`/tenants/${tenantId}/users`, { params }).then(r => normalizeList(r, 'users')),
  get: (tenantId, userId) => api.get(`/tenants/${tenantId}/users/${userId}`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/users`, data),
  update: (tenantId, userId, data) => api.patch(`/tenants/${tenantId}/users/${userId}`, data),
  delete:          (tenantId, userId, params)  => api.delete(`/tenants/${tenantId}/users/${userId}`, { params }),
  deletePermanent: (tenantId, userId)           => api.delete(`/tenants/${tenantId}/users/${userId}/permanent`),
  restore: (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/restore`),
  disconnect: (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/disconnect`),
  sync: (tenantId, userId) => api.post(`/tenants/${tenantId}/users/${userId}/sync`),
  renew: (tenantId, userId, params) => api.post(`/tenants/${tenantId}/users/${userId}/renew`, params),
  generateVouchers: (tenantId, data) => api.post(`/tenants/${tenantId}/users/generate_vouchers`, data).then(r => normalizeList(r, 'users')),
  // Test auth radius user langsung ke FreeRADIUS (radtest). Body: { password }.
  test: (tenantId, userId, data) => api.post(`/tenants/${tenantId}/users/${userId}/test`, data),
};

export const profilesAPI = {
  list: (tenantId, params) => api.get(`/tenants/${tenantId}/profiles`, { params }).then(r => normalizeList(r, 'profiles')),
  get: (tenantId, profileId) => api.get(`/tenants/${tenantId}/profiles/${profileId}`),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/profiles`, data),
  update: (tenantId, profileId, data) => api.patch(`/tenants/${tenantId}/profiles/${profileId}`, data),
  delete:          (tenantId, profileId, params) => api.delete(`/tenants/${tenantId}/profiles/${profileId}`, { params }),
  deletePermanent: (tenantId, profileId)          => api.delete(`/tenants/${tenantId}/profiles/${profileId}/permanent`),
  restore: (tenantId, profileId) => api.post(`/tenants/${tenantId}/profiles/${profileId}/restore`),
  sync: (tenantId, profileId) => api.post(`/tenants/${tenantId}/profiles/${profileId}/sync`),
};

export const nasAPI = {
  list: (tenantId, params) => api.get(`/tenants/${tenantId}/nas`, { params }).then(r => normalizeList(r, 'nas_devices')),
  create: (tenantId, data) => api.post(`/tenants/${tenantId}/nas`, data),
  get:              (tenantId, nasId)           => api.get(`/tenants/${tenantId}/nas/${nasId}`),
  update: (tenantId, nasId, data) => api.patch(`/tenants/${tenantId}/nas/${nasId}`, data),
  config:           (tenantId, nasId)           => api.get(`/tenants/${tenantId}/nas/${nasId}/config`),
  delete:          (tenantId, nasId, params)   => api.delete(`/tenants/${tenantId}/nas/${nasId}`, { params }),
  deletePermanent: (tenantId, nasId)            => api.delete(`/tenants/${tenantId}/nas/${nasId}/permanent`),
  restore: (tenantId, nasId) => api.post(`/tenants/${tenantId}/nas/${nasId}/restore`),
  sync: (tenantId, nasId) => api.post(`/tenants/${tenantId}/nas/${nasId}/sync`),
};

// Daftar instance server VPN (wireguard/ipsec) — global, bukan per-tenant.
export const vpnServersAPI = {
  list:            (params)       => api.get('/vpn-servers', { params }).then(r => normalizeList(r, 'vpn_servers')),
  get:              (id)           => api.get(`/vpn-servers/${id}`),
  create:           (data)         => api.post('/vpn-servers', data),
  update:           (id, data)     => api.patch(`/vpn-servers/${id}`, data),
  delete:           (id, params)   => api.delete(`/vpn-servers/${id}`, { params }),
  deletePermanent:  (id)           => api.delete(`/vpn-servers/${id}/permanent`),
  restore:          (id)           => api.post(`/vpn-servers/${id}/restore`),
  health:           (id)           => api.get(`/vpn-servers/${id}/health`),
  syncPubkey:       (id)           => api.post(`/vpn-servers/${id}/sync_pubkey`),
};

export const vpnWireguardAPI = {
  list:            (tenantId, params)         => api.get(`/tenants/${tenantId}/vpn/wireguard`, { params }).then(r => normalizeList(r, 'vpn_wireguard_peers')),
  availableIps:    (tenantId, vpnServerId)    => api.get(`/tenants/${tenantId}/vpn/wireguard/available_ips`, { params: { vpn_server_id: vpnServerId } }),
  status:          (tenantId, vpnServerId)    => api.get(`/tenants/${tenantId}/vpn/wireguard/status`, { params: { vpn_server_id: vpnServerId } }),
  create:          (tenantId, data)           => api.post(`/tenants/${tenantId}/vpn/wireguard`, data),
  get:              (tenantId, peerId)         => api.get(`/tenants/${tenantId}/vpn/wireguard/${peerId}`),
  update:           (tenantId, peerId, data)   => api.patch(`/tenants/${tenantId}/vpn/wireguard/${peerId}`, data),
  config:           (tenantId, peerId, params) => api.get(`/tenants/${tenantId}/vpn/wireguard/${peerId}/config`, { params }),
  delete:           (tenantId, peerId, params) => api.delete(`/tenants/${tenantId}/vpn/wireguard/${peerId}`, { params }),
  deletePermanent:  (tenantId, peerId)         => api.delete(`/tenants/${tenantId}/vpn/wireguard/${peerId}/permanent`),
  restore:          (tenantId, peerId)         => api.post(`/tenants/${tenantId}/vpn/wireguard/${peerId}/restore`),
  sync:             (tenantId, peerId)         => api.post(`/tenants/${tenantId}/vpn/wireguard/${peerId}/sync`),
};

export const vpnIpsecAPI = {
  list:            (tenantId, params)         => api.get(`/tenants/${tenantId}/vpn/ipsec`, { params }).then(r => normalizeList(r, 'vpn_ipsec_users')),
  availableIps:    (tenantId, vpnServerId)    => api.get(`/tenants/${tenantId}/vpn/ipsec/available_ips`, { params: { vpn_server_id: vpnServerId } }),
  status:          (tenantId, vpnServerId)    => api.get(`/tenants/${tenantId}/vpn/ipsec/status`, { params: { vpn_server_id: vpnServerId } }),
  create:          (tenantId, data)           => api.post(`/tenants/${tenantId}/vpn/ipsec`, data),
  get:              (tenantId, userId)         => api.get(`/tenants/${tenantId}/vpn/ipsec/${userId}`),
  update:           (tenantId, userId, data)   => api.patch(`/tenants/${tenantId}/vpn/ipsec/${userId}`, data),
  config:           (tenantId, userId, params) => api.get(`/tenants/${tenantId}/vpn/ipsec/${userId}/config`, { params }),
  delete:           (tenantId, userId, params) => api.delete(`/tenants/${tenantId}/vpn/ipsec/${userId}`, { params }),
  deletePermanent:  (tenantId, userId)         => api.delete(`/tenants/${tenantId}/vpn/ipsec/${userId}/permanent`),
  restore:          (tenantId, userId)         => api.post(`/tenants/${tenantId}/vpn/ipsec/${userId}/restore`),
  sync:             (tenantId, userId)         => api.post(`/tenants/${tenantId}/vpn/ipsec/${userId}/sync`),
};

export const syncAPI = {
  status: () => api.get('/sync/status'),
  run: () => api.post('/sync/run'),
  force: (data) => api.post('/sync/force', data),
  reconcile: (data) => api.post('/sync/reconcile', data),
  logs: (params) => api.get('/sync/logs', { params }).then(r => normalizeList(r, 'logs')),
  pending: () => api.get('/sync/pending'),
};

export const reportsAPI = {
  templates: {
    list:   ()          => api.get('/reports/templates').then(r => normalizeList(r, 'templates').templates || r),
    get:    (id)         => api.get(`/reports/templates/${id}`),
    create: (data)        => api.post('/reports/templates', data),
    update: (id, data)    => api.patch(`/reports/templates/${id}`, data),
    delete: (id)          => api.delete(`/reports/templates/${id}`),
  },
  // Backend sekarang selalu balikin JSON polos: { title, dataset, total, rows }.
  // Export ke CSV/XML/XLSX/PDF dikerjakan di frontend (lihat src/utils/reportExport.js).
  generate: (payload) => api.post('/reports/generate', payload),
};

export const authLogsAPI = {
  list: (params) => api.get('/auth-logs', { params }).then(r => normalizeList(r, 'auth_logs')),
};

export const changeLogsAPI = {
  list: (params) => api.get('/change-logs', { params }).then(r => normalizeList(r, 'change_logs')),
};

export const acctsAPI = {
  latest:     (tenantId, params)    => api.get(`/tenants/${tenantId}/accts/latest`, { params }),
  forceClose: (tenantId, radacctid) => api.post(`/tenants/${tenantId}/accts/force_close`, { radacctid }),
};

export const analyticsAPI = {
  histogram:  (params) => api.get('/analytics/histogram',  { params }),
  usage:      (params) => api.get('/analytics/usage',      { params }),
  concurrent: (params) => api.get('/analytics/concurrent', { params }),
  top:        (params) => api.get('/analytics/top',        { params }),
  history:    (params) => api.get('/analytics/history',    { params }),
};

export const adminUsersAPI = {
  list: (params) => api.get('/admin-users', { params }).then(r => normalizeList(r, 'admin_users')),
  get: (id) => api.get(`/admin-users/${id}`),
  create: (data) => api.post('/admin-users', data),
  update: (id, data) => api.patch(`/admin-users/${id}`, data),
  delete: (id) => api.delete(`/admin-users/${id}`),
  restore: (id) => api.post(`/admin-users/${id}/restore`),
};

export const tokensAPI = {
  list: () => api.get('/tokens').then(r => normalizeList(r, 'tokens')),
  get: (id) => api.get(`/tokens/${id}`),
  create: (data) => api.post('/tokens', data),
  revoke: (id) => api.delete(`/tokens/${id}`),
};

export const workerQueryAPI = {
  tables: (serverId) => api.get(`/radius-servers/${serverId}/query/tables`),
  query: (serverId, table, params) => api.get(`/radius-servers/${serverId}/query/${table}`, { params }),
};

export const dashboardTabsAPI = {
  list:    ()              => api.get('/dashboard-tabs'),
  create:  (data)          => api.post('/dashboard-tabs', data),
  update:  (id, data)      => api.patch(`/dashboard-tabs/${id}`, data),
  delete:  (id)            => api.delete(`/dashboard-tabs/${id}`),
  reorder: (tabs)          => api.put('/dashboard-tabs/reorder', { tabs }),
};

export default api;